import { type PropsWithChildren, useContext, createContext } from 'react';

import {
  type Cell,
  type Column,
  type Row,
  type Table,
} from '../hooks/useTable/types.js';
import { DataTableV2ColumnSettingsConfig } from '../public.api.js';

//#region table context
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const DataTableV2Context = createContext<Table<any> | null>(null);

/** @internal */
export function DataTableV2ContextProvider<T>(
  props: PropsWithChildren<{ value: Table<T> }>,
) {
  return (
    <DataTableV2Context.Provider value={props.value}>
      {props.children}
    </DataTableV2Context.Provider>
  );
}

/** @internal */
export function useDataTableV2<T>() {
  const table = useContext<Table<T> | null>(DataTableV2Context);

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (table === null) {
    throw new Error('Table instance is not defined in context.');
  }

  return table;
}
//#endregion

//#region column context
/** @internal */
export const DataTableV2ColumnContext = createContext<Column<
  unknown,
  unknown
> | null>(null);

/** @internal */
export function DataTableV2ColumnContextProvider(
  props: PropsWithChildren<{ value?: Column<unknown, unknown> }>,
) {
  return (
    <DataTableV2ColumnContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableV2ColumnContext.Provider>
  );
}

/** @internal */
export function useDataTableV2Column() {
  const column = useContext<Column<unknown, unknown> | null>(
    DataTableV2ColumnContext,
  );

  // Typescript assertion function, as in the context, the table will
  // always be defined.
  if (column === null) {
    throw new Error('Column instance is not defined in context.');
  }

  return column;
}
//#endregion

//#region row context
/** @internal */
export const DataTableV2RowContext = createContext<Row<unknown> | null>(null);

/** @internal */
export function DataTableV2RowContextProvider(
  props: PropsWithChildren<{ value?: Row<unknown> }>,
) {
  return (
    <DataTableV2RowContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableV2RowContext.Provider>
  );
}

/** @internal */
export function useDataTableV2Row() {
  const row = useContext<Row<unknown> | null>(DataTableV2RowContext);

  return row;
}
//#endregion

//#region cell context
/** @internal */
export const DataTableV2CellContext = createContext<Cell<
  unknown,
  unknown
> | null>(null);

/** @internal */
export function DataTableV2CellContextProvider(
  props: PropsWithChildren<{ value?: Cell<unknown, unknown> }>,
) {
  return (
    <DataTableV2CellContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableV2CellContext.Provider>
  );
}

/** @internal */
export function useDataTableV2Cell() {
  const cell = useContext<Cell<unknown, unknown> | null>(
    DataTableV2CellContext,
  );

  return cell;
}
//#endregion

//#region column settings context
/** @internal */
export type DataTableV2ColumnSettingsHandle = {
  openColumnSettings?: (
    columnSettings?: DataTableV2ColumnSettingsConfig,
  ) => void;
};

/** @internal */
export const DataTableV2ColumnSettingsContext =
  createContext<DataTableV2ColumnSettingsHandle | null>(null);

/** @internal */
export function DataTableV2ColumnSettingsContextProvider(
  props: PropsWithChildren<{
    value?: DataTableV2ColumnSettingsHandle;
  }>,
) {
  return (
    <DataTableV2ColumnSettingsContext.Provider value={props.value ?? null}>
      {props.children}
    </DataTableV2ColumnSettingsContext.Provider>
  );
}

/** @internal */
export function useDataTableV2ColumnSettings() {
  return useContext<DataTableV2ColumnSettingsHandle | null>(
    DataTableV2ColumnSettingsContext,
  );
}
//#endregion

//#region isHeader context
const DataTableV2IsHeaderContext = createContext(false);

/** @internal */
export function DataTableV2IsHeaderContextProvider(props: PropsWithChildren) {
  return (
    <DataTableV2IsHeaderContext.Provider value={true}>
      {props.children}
    </DataTableV2IsHeaderContext.Provider>
  );
}

/** @internal */
export function useDataTableV2IsHeaderContextProvider() {
  return useContext<boolean>(DataTableV2IsHeaderContext);
}
